/**
 * 返回导航：**返回目标由路由层级推导**，不由浏览器历史决定。
 *
 * ## 为什么要有这个模块（2026-10 用户真机实测）
 *
 * 用户在手机上连着发了两张截图：
 *
 *   1. 孩子详情页（`/students/:id`）：左上角**没有**箭头，右上角有一个「返回」按钮；
 *   2. 学校作业页（`/students/:id/homework`）：左上角有「←」，右上角**又有一个**「返回」。
 *
 * 于是两个问题：
 *
 *   - **入口重复**：同一屏上两个"返回"（`←` 与「返回」），用户原话是
 *     "返回键和箭头的功能好像有点重复，要么就保留箭头，要么就保留返回键吧"。
 *   - **来回弹（死循环）**：孩子详情页的「返回」当时写的是 `navigate(-1)`
 *     （**浏览器历史后退**）。历史里刚离开的一页很可能就是它的**下钻子页**
 *     （例如刚从"学校作业"点进来），于是：孩子详情 → 后退 → 学校作业 →
 *     点「返回」 → 孩子详情 → 后退 → 学校作业 …… 用户原话是
 *     "点返回键的话，会在两个页面之间不断切换"。
 *
 * ## 三个不变量（本模块存在的全部意义）
 *
 * 1. **唯一入口**：一个页面最多只有一个「返回/后退」入口，而它由
 *    `AppShell` 依据**当前路由**统一渲染（左上角 `←` 箭头，手机上的标准位置）。
 *    页面**不再自己传 `backTo`**，也不可能自己再摆一个「返回」按钮 ——
 *    唯一的例外是 `backLabel`：那个标签是给"相关页面"跳转用的（事实存在的横向链接，
 *    例如打卡任务页 → 目标页），**不是**返回入口，所以它的文案里不许出现「返回」。
 * 2. **不成环**：返回目标是**层级上明确的上一级**（`/a/b/c` 的上一级是 `/a/b`），
 *    因此沿返回链往上走，层级**严格递减**，必然在有限步内到达顶层。
 * 3. **目标页真的存在**：每条返回目标都必须是**已注册路由**
 *    （`src/routes.ts` 的 `REGISTERED_ROUTE_PATHS`）。这一条是 2026-10 补上的：
 *    当时孩子详情的返回目标写成了 `/students` —— **语义上说得通、路由表里却没有**，
 *    客户端 `<Link>` 同样要过匹配表 → 落 `*` 兜底 → 用户在真机上看到「页面不存在」。
 *    记住：**返回目标不是字符串，是一个必须能被路由匹配到的地址。**
 *
 *    `backNav.test.ts` 把这三条都锁住了（含实际页面用例、全站映射表遍历、
 *    以及"目标必须是注册路由"的逐条核对）。
 *
 * ## 为什么用"层级推导"而不是"历史后退"
 *
 * `navigate(-1)` 的结果取决于**用户怎么走到这一页的**，而那是任意长度的历史序列：
 * 从孩子详情页钻进来的可能来自首页、作业页、统计页……"后退"于是既不确定、又可能
 * 退到浏览器外部或已失效的表单状态。层级推导只依赖路由本身：同样的页面永远回同一处，
 * 与历史无关，也不可能成环。
 *
 * ## 顶层页面没有箭头（**这是有意的**，不是漏掉了）
 *
 * `/`、`/tools`、`/rewards`、`/settings`、`/student`、`/student/*` 这些页面在
 * 主导航（PC 侧边栏 / 手机底部 tab）里都有直接入口，给它们再配一个 `←` 只会让头部更挤。
 * 真正需要箭头的是那些**不在主导航里、只能从某页钻进去**的页面：
 * `/tools/dictation`，以及 `/students/:id` 与其下的
 * `homework · nodes · habits · points · rewards · redemptions · stats`。
 */
import {
  REGISTERED_ROUTE_PATHS,
  type RegisteredRoutePath,
} from '../routes';

/**
 * 一行的"顶层锚点"：`parent === null` 表示不再有上一级。
 *
 * 注意 `title` 是**导航里的名字**（列表页叫「孩子列表」，而不是它标题栏上的
 * 「我的孩子」）：它会被拼成返回入口的 `aria-label`，而「返回我的孩子」
 * 读起来像自指。页面标题栏上的文字仍由各页面自己传 `title`，两处不互相绑架。
 */
export interface BackRow {
  /** 路由模式（`:id` 是动态段）：**必须在 `REGISTERED_ROUTE_PATHS` 里**。 */
  pattern: RegisteredRoutePath;
  /**
   * 上一级的路由模式；`null` = 顶层（没有返回入口）。
   *
   * ⚠️ 类型是 `RegisteredRoutePath` —— **没注册过的路径这里根本编译不过**。
   * 2026-10 生产事故：这里曾写成 `/students`（"语义上该有"、路由表里却没有），
   * 客户端 `<Link>` 同样要过路由匹配表 → 落 `*` 兜底 → 真机上点「←」看到
   * 「页面不存在」。那次 `tsc` 全绿，是因为当时这两个字段都只是 `string`。
   * 现在三重保险：**编译期**（本类型）+ **测试期**（`backNav.test.ts` 逐条核对
   * 目标在名单里，并与 `App.tsx` 源码双向比对）+ **运行期**（`routePath()`）。
   */
  parent: RegisteredRoutePath | null;
  /** 这一页在导航里的名字：返回链的说明文字 + 返回入口的 `aria-label`。 */
  title: string;
}

/**
 * **全站唯一的"路由 → 上一级"映射表。**
 *
 * 顺序有讲究：**具体路由在通用路由之前**。匹配是逐个"段"比的，所以
 * `/students/:id` 不会错吃掉 `/students/:id/homework`（第三段不同），
 * 但同一层的长度顺序仍按"长者在前"排，读起来也更清楚。
 */
export const BACK_ROWS: readonly BackRow[] = [
  // ---------- 无孩子 id：都在主导航里，或本来就是顶层 ----------
  { pattern: '/', parent: null, title: '孩子列表' },
  /**
   * 「工具」：主导航第三格（2026-10-14 由「共享」改来），所以它是顶层、没有箭头。
   * 里面的共享收件箱**不是**一个单独的路由，而是这一页的一段 —— 于是
   * "收件箱还在，且仍然到得了"这件事不依赖任何返回链。
   */
  { pattern: '/tools', parent: null, title: '工具' },
  /**
   * 语文听写（录入界面）——**顶层工具页**。返回目标是「工具」列表页。
   *
   * ⚠️ 它原来挂在 `/students/:id/dictation`（上一级是孩子详情），2026-10-14 搬到
   *   顶层：字表是全家的（听写 6 张表里没有 `student_id`），挂到某个孩子下面会让
   *   家长以为是"这个孩子专属的字表"。路径里不再有 `:id`；后端接口仍要一个孩子，
   *   那由页面**内部**用 `?anchor=` 带上（界面上不暴露，见 `lib/dictationAnchor.ts`）。
   */
  { pattern: '/tools/dictation', parent: '/tools', title: '语文听写' },
  /*
    ---------- 语文听写（**孩子下面**的那三页，2026-10-15 新增） ----------
    这一组是"某个孩子的听写"，与上面那条顶层工具页**不是同一件事**：

      · `/tools/dictation`        —— 数据录入（字表），全家共用；
      · `/students/:id/dictation` —— 听写设置 / 布置 / 做听写 / 历史，属于这个孩子。

    ⚠️ 顺序有讲究：`/students/:id/dictation` 是 `/students/:id` 的**子级**，
       而 `/students/:id/dictation/:sessionId/review` 又是它的子级。
       `matchPattern` 逐段比且段数必须相等，所以三者不会互相吃掉
       （第 4 段分别是 `dictation` / `:sessionId` / `:sessionId`，但段数不同：
        4 段 / 5 段 / 6 段）。仍然按"长者在前"排，读起来更清楚。
  */
  { pattern: '/students/:id/dictation/:sessionId/review', parent: '/students/:id/dictation/:sessionId', title: '这次听写' },
  /**
   * 预习卡：**这次听写**的下钻子页（在"开始播放"之前看笔顺）。
   *
   * 上一级必须是 `/students/:id/dictation/:sessionId`（播放器那一页）：
   * 预习完就回到那里点「开始播放」，回到"听写列表"会让家长自己重新找这次会话。
   * 段数与 `review` 相同（6 段），第 5 段是静态段，不会互相吃掉。
   */
  { pattern: '/students/:id/dictation/:sessionId/preview', parent: '/students/:id/dictation/:sessionId', title: '先预习一下' },
  { pattern: '/students/:id/dictation/:sessionId', parent: '/students/:id/dictation', title: '语文听写' },
  /**
   * 错词本：**挂在"这个孩子的听写"下面**（它是孩子的数据），所以返回目标是
   * `/students/:id/dictation` —— 层级严格递减，不成环。
   *
   * ⚠️ 顺序：它与 `/students/:id/dictation/:sessionId` 段数相同（5 段），
   *    第 4 段是静态的 `mistakes`。`matchPattern` 逐段比，静态段不会匹配 `mistakes`
   *    以外的值，所以两者不会互相吃掉；按"具体在前"排读起来更清楚。
   */
  { pattern: '/students/:id/dictation/mistakes', parent: '/students/:id/dictation', title: '错词本' },
  { pattern: '/students/:id/dictation', parent: '/students/:id', title: '语文听写' },
  { pattern: '/rewards', parent: null, title: '奖品目录（全家共用）' },
  { pattern: '/settings', parent: null, title: '账号设置' },
  { pattern: '/404', parent: null, title: '页面不存在' },

  // ---------- 家长端：孩子详情是这一层的"根"，它自己回孩子**列表** ----------
  //   列表就是首页 `/`（`ParentHomePage`，标题栏写着「我的孩子」）。
  //   ⚠️ 这里曾经写的是 `/students`：那是"列表的语义路径"，但**它没有注册路由**，
  //   于是真机上点「←」落到 `*` 兜底 → 「页面不存在」（2026-10 用户实测）。
  //   返回目标只认 `App.tsx` 里注册过的路径，不能凭语义想象。
  { pattern: '/students/:id', parent: '/', title: '孩子详情' },
  { pattern: '/students/:id/homework', parent: '/students/:id', title: '学校作业' },
  { pattern: '/students/:id/nodes', parent: '/students/:id', title: '目标' },
  { pattern: '/students/:id/points', parent: '/students/:id', title: '积分明细' },
  { pattern: '/students/:id/rewards', parent: '/students/:id', title: '奖品目录' },
  { pattern: '/students/:id/redemptions', parent: '/students/:id', title: '兑换审批' },
  { pattern: '/students/:id/stats', parent: '/students/:id', title: '统计' },

  // ---------- 学生端：全部都在底部 tab 里，所以都是顶层（没有箭头） ----------
  { pattern: '/student', parent: null, title: '今日打卡' },
  { pattern: '/student/homework', parent: null, title: '我的作业' },
  /**
   * 语文听写（孩子端，迁移 0020）。
   *
   * · 列表页与底部 tab 里的页面同级（从「今日打卡」的听写卡片点进来，
   *   但它是**独立的一页**，所以 `parent: null` —— 底部 tab 就是它的家）；
   * · 播放器（`:sessionId`）的上一级是列表页 —— 孩子从播放器退出来应该回到
   *   "还有哪些任务"，而不是回到打卡页；
   * · 预习卡（`:sessionId/preview`）的上一级是**播放器**（与家长端同一条规则：
   *   预习完就回去按「开始播放」）。段数 5 > 4，返回链严格递减。
   */
  { pattern: '/student/dictation', parent: null, title: '我的听写' },
  { pattern: '/student/dictation/:sessionId', parent: '/student/dictation', title: '我的听写' },
  {
    pattern: '/student/dictation/:sessionId/preview',
    parent: '/student/dictation/:sessionId',
    title: '先预习一下',
  },
  { pattern: '/student/points', parent: null, title: '我的积分' },
  { pattern: '/student/rewards', parent: null, title: '奖品屋' },
  { pattern: '/student/settings', parent: null, title: '账号设置' },
];

/**
 * 顶层锚点的**显示名**。
 *
 * 它们自身没有返回入口（在主导航里，`parent: null`），但会作为别的页面的返回落点
 * 出现：`/` 是孩子详情的落点（孩子列表），`/tools` 是语文听写的落点（工具列表），
 * `/student` 是学生端的根。
 *
 * ⚠️ key 类型同样是 `RegisteredRoutePath`：`/students` 那种"语义路径"在这里也编译不过。
 */
const ANCHOR_TITLES: Partial<Record<RegisteredRoutePath, string>> = {
  '/': '孩子列表',
  '/tools': '工具',
  '/student': '今日打卡',
};

export interface BackLink {
  /** 可以直接交给 `<Link to>` 的具体路径（动态段已填好）。 */
  to: string;
  /** 目标页在导航里的名字。 */
  title: string;
  /** 无障碍标签，例如「返回孩子详情」。 */
  label: string;
}

// ---------------------------------------------------------------- 路径工具

/** 切成非空段：`/students/x/` → `['students','x']`。 */
function segmentsOf(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/** 只做路径匹配，忽略查询串、hash 与结尾斜杠。 */
function normalizePath(path: string): string {
  const [withoutHash = ''] = path.split('#');
  const [withoutQuery = ''] = withoutHash.split('?');
  return withoutQuery;
}

/**
 * 路径是否匹配模式；命中时返回捕获到的动态段。
 * 段数必须相等 —— 所以 `/students/x` **不会**匹配 `/students/:id/homework`。
 */
function matchPattern(pattern: string, path: string): Record<string, string> | null {
  const p = segmentsOf(normalizePath(pattern));
  const s = segmentsOf(normalizePath(path));
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i += 1) {
    const patternSeg = p[i]!;
    const value = s[i]!;
    if (patternSeg.startsWith(':')) params[patternSeg.slice(1)] = value;
    else if (patternSeg !== value) return null;
  }
  return params;
}

/** 把模式里的动态段换成实际值：`/students/:id` + `{id:'x'}` → `/students/x`。 */
function resolvePattern(pattern: string, params: Record<string, string>): string {
  return (
    '/' +
    segmentsOf(pattern)
      .map((seg) => (seg.startsWith(':') ? (params[seg.slice(1)] ?? seg) : seg))
      .join('/')
  );
}

function findRow(path: string): { row: BackRow; params: Record<string, string> } | null {
  for (const row of BACK_ROWS) {
    const params = matchPattern(row.pattern, path);
    if (params) return { row, params };
  }
  return null;
}

function linkTo(pattern: RegisteredRoutePath, params: Record<string, string>): BackLink {
  const to = resolvePattern(pattern, params);
  // `to` 一定是注册路由拼出来的，所以可以按 `RegisteredRoutePath` 索引锚点表。
  const anchor = ANCHOR_TITLES[to as RegisteredRoutePath];
  const title = findRow(to)?.row.title ?? anchor ?? '';
  return {
    to,
    title,
    label: title ? `返回${title}` : '返回',
  };
}

// ---------------------------------------------------------------- 运行时

/**
 * 当前路径的**返回入口**；`null` = 这一页是顶层（不渲染 `←`）。
 *
 * @param pathname 当前 `location.pathname`（查询串会被忽略）
 */
export function backToFor(pathname: string): BackLink | null {
  const path = normalizePath(pathname);

  const hit = findRow(path);
  if (!hit) return null;
  if (!hit.row.parent) return null;
  return linkTo(hit.row.parent, hit.params);
}
